import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    CompleteSortieCommand,
    SortieDto
} from '@flyfreely-portal-ui/flyfreely';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MissionRecordService } from '../../../mission-record-edit/mission-record.service';
import {
    DisplayableResource,
    MissionCompletionService
} from '../../mission-completion.service';
import { FlightLogFileWithRpa } from '../../mission-flight-logs.directive';
import { MatchesExistingFlightPipe } from '../matches-existing-flight.pipe';

interface SortiesMatchingLog extends CompleteSortieCommand {
    matchesLog: boolean;
}

@Component({
    selector: 'assign-to-flight-dialogue',
    template: `
        <div class="input-group" style="min-width: 250px;">
            <ng-select
                [items]="flightsWithLogMatch"
                [(ngModel)]="selectedFlight"
                bindValue="id"
                (click)="$event.stopPropagation()"
            >
                <ng-template ng-option-tmp let-item="item">
                    <div
                        [tooltip]="
                            item.matchesLog
                                ? 'This flight matches the flight log'
                                : ''
                        "
                        placement="right"
                    >
                        {{ getFlightLabel(item) }}&nbsp;
                        <span
                            *ngIf="item.matchesLog"
                            class="fal fa-plane-circle-check text-success"
                        ></span>
                    </div>
                </ng-template>
                <ng-template ng-label-tmp let-item="item">
                    <div
                        [tooltip]="
                            item.matchesLog
                                ? 'This flight matches the flight log'
                                : ''
                        "
                        placement="right"
                    >
                        {{ getFlightLabel(item) }}&nbsp;
                        <span
                            *ngIf="item.matchesLog"
                            class="fal fa-plane-circle-check text-success"
                        ></span>
                    </div>
                </ng-template>
            </ng-select>

            <button
                type="button"
                class="btn btn-primary pull-right top-buffer"
                (click)="assignToFlight.emit(selectedFlight)"
                [disabled]="selectedFlight == null"
            >
                Assign
            </button>
            <button
                type="button"
                class="btn btn-default pull-right top-buffer"
                (click)="closePopover.emit()"
            >
                Cancel
            </button>
        </div>
    `
})
export class AssignToFlightDialogue {
    @Input() historicalMission: boolean;
    @Input() flightLog: FlightLogFileWithRpa;
    @Output() assignToFlight = new EventEmitter<number>();
    @Output() closePopover = new EventEmitter<void>();

    allFlights: CompleteSortieCommand[] = [];
    flightsWithLogMatch: SortiesMatchingLog[] = [];
    selectedFlight: number;

    availablePersonnel: DisplayableResource[] = [];

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private missionCompletionService: MissionCompletionService,
        private missionRecordService: MissionRecordService,
        private matchesExistingFlightPipe: MatchesExistingFlightPipe
    ) {}

    ngOnInit() {
        this.refreshLists();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshLists() {
        if (this.historicalMission) {
            this.allFlights =
                this.missionRecordService.form.controls.flights.value ?? [];

            this.missionRecordService.form.controls.flights.valueChanges
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(flights => {
                    this.allFlights = flights;
                    this.buildFlightsMatchingLog();
                });

            this.missionRecordService.availablePersonnel$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(personnel => (this.availablePersonnel = personnel));
        } else {
            this.allFlights =
                this.missionCompletionService.form.controls.flights.value ?? [];

            this.missionCompletionService.form.controls.flights.valueChanges
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(flights => {
                    this.allFlights = flights;
                    this.buildFlightsMatchingLog();
                });

            this.missionCompletionService.availablePersonnel$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(personnel => (this.availablePersonnel = personnel));
        }
        this.buildFlightsMatchingLog();
    }

    buildFlightsMatchingLog() {
        if (this.flightLog == null) {
            this.flightsWithLogMatch = this.allFlights.map(f => ({
                ...f,
                matchesLog: false
            }));
        } else {
            this.flightsWithLogMatch = this.allFlights.map(f => ({
                ...f,
                matchesLog: this.matchesExistingFlightPipe.transform(
                    this.flightLog,
                    [f as SortieDto]
                )
            }));
        }
        this.selectedFlight = this.flightsWithLogMatch.find(
            f => f.matchesLog
        )?.id;
    }

    getFlightLabel(flight: CompleteSortieCommand) {
        const flightNumber = `Flight ${flight.number}`;
        const pilotDetails =
            flight.pilotId != null
                ? `, RP - ${
                      this.availablePersonnel.find(p => p.id === flight.pilotId)
                          .name
                  }`
                : '';
        return flightNumber + pilotDetails;
    }
}
